import { ContentFactory, Schema, Content, ContentList, SchemaService, ContentService } from '../../squidex';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ID } from '@datorama/akita';
import { FilterPagination } from './filter.pagination';
import { ESchemaContentStatus } from './enum';


export abstract class BaseService {
  protected contentService: ContentService;

  protected constructor(
    readonly schemaName: string,
    readonly contentFactory: ContentFactory) {
  }

  protected initialize(): void {
    if (!this.contentService) {
      this.contentService = this.contentFactory.getContentService(this.schemaName);
    }
  }

  getAll(): Observable<ContentList> {
    this.initialize();
    return this.contentService.getAllContent();
  }

  getAllLanguages(): Observable<any> {
    this.initialize();
    return this.contentService.getAllLanguages();
  }


  doPagination(filterPagination: FilterPagination): Observable<ContentList> {
    this.initialize();

    return this.contentService.findContent(filterPagination.getODataQuery());
  }

  getTopX(top: number, filter: string, orderBy: string, contentStatus: ESchemaContentStatus = ESchemaContentStatus.Published): Observable<ContentList> {
    this.initialize();

    let publishedText = "";

    switch (contentStatus) {
      case ESchemaContentStatus.All: {
        publishedText = "and (status eq 'Published' or status eq 'Draft')";
        break;
      }
      case ESchemaContentStatus.Published: {
        publishedText = "and (status eq 'Published')";
        break;
      }
      case ESchemaContentStatus.Draft: {
        publishedText = "and (status eq 'Draft')";
        break;
      }
      default: {
        publishedText = "";
        break;
      }
    }

    let f = '';

    if (filter) {
      f = `?$filter=(data/deleted/iv ne true ) ${publishedText} ${filter}  &$top=${top}&$orderby=${orderBy}`;
    } else {
      f = `?$filter=(data/deleted/iv ne true ) ${publishedText}  &$top=${top}&$orderby=${orderBy}`;
    }

    return this.contentService.findContent(f);
  }

  getFiltered(top: number, orderBy: string, contentStatus: ESchemaContentStatus, filterTextValue?: string): Observable<ContentList> {
    this.initialize();

    let publishedText = "";

    switch (contentStatus) {
      case ESchemaContentStatus.All: {
        // publishedText = "and (status eq 'Published' or status eq 'Draft')";
        break;
      }
      case ESchemaContentStatus.Published: {
        publishedText = "Published";
        break;
      }
      case ESchemaContentStatus.Draft: {
        publishedText = "Draft";
        break;
      }
      default: {
        publishedText = "";
        break;
      }
    }

    let contentObject =
    {
      filter:
      {
        and:
          [
            {
              path: "data.deleted.iv",
              op: "eq",
              value: false
            },
            {
              path: "status",
              op: "eq",
              value: publishedText
            }
          ]
      }
    }


    let sortInformation = orderBy.split(" ");

    if (sortInformation.length === 1) {
      contentObject["sort"] = [{ path: sortInformation[0] }];
    } else if (sortInformation.length === 2) {
      contentObject["sort"] = [{
        path: sortInformation[0],
        order: sortInformation[1],
      }];
    }

    contentObject["take"] = top;

    if (filterTextValue && filterTextValue != "") {
      contentObject["fullText"] = filterTextValue + "*";
    }

    var a = JSON.stringify(contentObject);
    let filterText = "?q=" + encodeURIComponent(JSON.stringify(contentObject));
    return this.contentService.findContent(filterText);
  }

  validateUrl(filterParms: FilterPagination,
    eSchemaContentStatus: ESchemaContentStatus = ESchemaContentStatus.Published,
    AdvancedFilter?: any, content: Content = null
  ): Observable<Number> {
    this.initialize();

    let publishedText = "";
    let opPublish = "";
    let unPublish = false;

    if (eSchemaContentStatus === ESchemaContentStatus.Published) {
      publishedText = "Published";
      opPublish = "eq";
    } else if (eSchemaContentStatus === ESchemaContentStatus.Draft) {
      publishedText = "Draft";
      unPublish = true;
      opPublish = "eq";
    }

    let contentObject =
    {
      filter:
      {
        and:
          [
            {
              path: "data.deleted.iv",
              op: "ne",
              value: true
            }
          ]
      }
    }

    if (eSchemaContentStatus !== ESchemaContentStatus.All) {
      let status: any = {};
      status = {
        path: "status",
          op: opPublish,
            value: publishedText
      }
      contentObject.filter.and.push(status);
    }


    if (content) {
      let orFilter: any = {};
      orFilter = {
        path: "id",
        op: "ne",
        value: content.id
      }
      contentObject.filter.and.push(orFilter);
    }

    if (AdvancedFilter) {
      contentObject.filter.and.push(AdvancedFilter);
    }

    let sortInformation = filterParms.orderBy.split(" ");

    if (sortInformation.length === 1) {
      contentObject["sort"] = [{ path: sortInformation[0] }];
    } else if (sortInformation.length === 2) {
      contentObject["sort"] = [{
        path: sortInformation[0],
        order: sortInformation[1],
      }];
    }

    contentObject["skip"] = filterParms.from;
    contentObject["take"] = filterParms.to - filterParms.from;

    if (typeof filterParms.filter != "undefined" && filterParms.filter != "") {
      contentObject["fullText"] = filterParms.filter + "*";
    }

    let filterText = "?q=" + encodeURIComponent(JSON.stringify(contentObject));
    return this.contentService.findContent(filterText, unPublish).pipe(map(content => {
      // console.log(content);
      return content['total'];
    }));;
  }

  getFilteredPagination(filterParms: FilterPagination = null,
    eSchemaContentStatus: ESchemaContentStatus = ESchemaContentStatus.Published,
    advancedFilter: Array<any> = null
  ): Observable<ContentList> {
    this.initialize();

    const draftMode = eSchemaContentStatus == ESchemaContentStatus.Published ? false : true;

    let publishedText = "";
    let opPublish = "";

    switch (eSchemaContentStatus) {
      case ESchemaContentStatus.All: {
        publishedText = "";
        opPublish = "ne";
        break;
      }
      case ESchemaContentStatus.Published: {
        publishedText = "Published";
        opPublish = "eq";
        break;
      }
      case ESchemaContentStatus.Draft: {
        publishedText = "Draft";
        opPublish = "eq";
        break;
      }
      default: {
        publishedText = "";
        opPublish = "ne";
        break;
      }
    }

    let contentObject =
    {
      filter:
      {
        and:
          [
            {
              path: "data.deleted.iv",
              op: "ne",
              value: true
            },
            {
              path: "status",
              op: opPublish,
              value: publishedText
            }
          ]
      },
      sort: []
    }

    if (advancedFilter) {
      if (Array.isArray(advancedFilter)) {
        advancedFilter.forEach(v => {
          contentObject.filter.and.push(v);
        });
      } else {
        contentObject.filter.and.push(advancedFilter);
      }
    }

    if (filterParms) {
      if (filterParms.orderBy && filterParms.orderBy !== '') {
        let idx: number = 0;
        let orderings = filterParms.orderBy.split(',');
        for (const ordering of orderings) {
          let sortInformation = ordering.split(" ");

          if (sortInformation.length === 1) {
            contentObject.sort.push({ path: sortInformation[0] });
          } else if (sortInformation.length === 2) {
            contentObject.sort.push({ path: sortInformation[0], order: sortInformation[1] });
          }
        }
      }

      contentObject["skip"] = filterParms.from;
      contentObject["take"] = filterParms.to - filterParms.from;

      if (filterParms.filter) {
        contentObject["fullText"] = filterParms.filter;
      }
    }
    let filterText = "?q=" + encodeURIComponent(JSON.stringify(contentObject));
    return this.contentService.findContent(filterText, draftMode);
  }

  getByObject(advancedFilter: any,
    eSchemaContentStatus: ESchemaContentStatus = ESchemaContentStatus.Published,
  ): Observable<ContentList> {
    this.initialize();

    const draftMode = eSchemaContentStatus == ESchemaContentStatus.Published ? false : true;

    let contentObject =
    {
      filter:
      {
        and:
          [
            {
              path: "data.deleted.iv",
              op: "ne",
              value: true
            }
          ]
      },
      sort: []
    }

    if (advancedFilter) {
      if (Array.isArray(advancedFilter)) {
        advancedFilter.forEach(v => {
          contentObject.filter.and.push(v);
        });
      } else {
        contentObject.filter.and.push(advancedFilter);
      }
    }

    let filterText = "?q=" + encodeURIComponent(JSON.stringify(contentObject));
    return this.contentService.findContent(filterText, draftMode);
  }

  getUnpublished(orderBy: string, top: number): Observable<ContentList> {
    this.initialize();
    if (top === -1) {
      return this.contentService.findContent(`?$filter=(status eq 'Draft')&$orderby=${orderBy}`);
    }
    return this.contentService.findContent(`?$filter=(status eq 'Draft') and (data/deleted/iv ne true ) &$top=${top}&$orderby=${orderBy}`);
  }

  updateContent(contentId: string, contentData: any): Observable<Content> {
    this.initialize();
    return this.contentService.updateContent(contentId, contentData);
  }

  createContent(contentData: any): Observable<Content> {
    this.initialize();
    return this.contentService.createContent(contentData);
  }

  createDraft(contentId: string): Observable<Content> {
    this.initialize();
    return this.contentService.createDraft(contentId);
  }

  deleteDraft(contentId: string): Observable<Content> {
    this.initialize();
    return this.contentService.deleteDraft(contentId);
  }

  removeContent(id: ID, draftMode: boolean = false): Observable<Content> {
    this.initialize();
    return this.contentService.softDeleteContent(id.toString(), draftMode)
  }

  removeHardContent(id: ID): Observable<Content> {
    this.initialize();
    return this.contentService.deleteContent(id.toString());
  }

  getContentById(id: ID, draftMode: boolean = false): Observable<Content> {
    this.initialize();
    return this.contentService.findContent(`?$filter=id eq '${id.toString()}' and (data/deleted/iv ne true )`, draftMode)
      .pipe(map(answer => {
        const item = answer['items'][0];
        if (item) {
          return item as Content;
        }
        return null;
      }));
  }

  getContentByIds(ids: string[], orderBy: string): Observable<Content[]> {
    this.initialize();
    let query = '?$filter=';

    ids.forEach(id => {
      query += `id eq '${id.toString()}' or `;
    });

    query = query.substring(0, query.length - 4);

    query += ` and (data/deleted/iv ne true )&$orderby=${orderBy}`;

    return this.contentService.findContent(query)
      .pipe(map(answer => {
        return answer['items'];
      }));
  }

  find(query: string): Observable<Content[]> {
    return this.contentService.findContent(query).pipe(map(items => items.items));
  }

  countContent(): Observable<Number> {
    this.initialize();
    return this.contentService.countContent();
  }

  addContent(contentData: any): Observable<Content> {
    this.initialize();
    return this.contentService.createContent(contentData);
  }

  createAndPublishContent(contentData: any): Observable<Content> {
    this.initialize();
    return this.contentService.createContentAndPublish(contentData);
  }

  getContentByCode(code: string, draftMode: boolean = false): Observable<Content> {
    this.initialize();
    return this.contentService.findContent(`?$filter=data/code/iv eq '${code}' and (data/deleted/iv ne true )`, draftMode)
      .pipe(map(answer => {
        const item = answer['items'][0];
        if (item) {
          return item as Content;
        }
        return null;
      }));
  }

  getByParentId(id: string, parentFieldName: string, orderBy: string = 'id', draftMode: boolean = false): Observable<ContentList> {
    this.initialize();
    return this.contentService.findContent(`?$filter=data/${parentFieldName}/iv eq '${id}' and (data/deleted/iv ne true ) &$orderby=${orderBy}`, draftMode);
  }

  getAllOrdered(orderBy: string = 'data/code/iv'): Observable<ContentList> {
    this.initialize();
    return this.contentService.getAllContentOrdered(orderBy);
  }

  updateContentStatus(contentId: string, status: string, dueTime: string) {
    this.initialize();
    return this.contentService.updateContentStatus(contentId, status, dueTime);
  }

  //TODO: to be fixed
  filterContent(filter: string, top: number, orderBy: string): Observable<ContentList> {
    this.initialize();
    return this.contentService.findContent(`?$filter=contains(data,'${filter}')`);
  }


  createLanguageDefinition(language: any): Observable<Content> {
    this.initialize();
    return this.contentService.createLanguageDefinition(language);
  }


  deleteLanguageDefinition(code: any): Observable<any> {
    this.initialize();
    return this.contentService.deleteLanguageDefinition(code);
  }

}

